import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Zap, Globe, Shield, Database, Code, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

export default function NewScan() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    target: '',
    scanType: 'web-app',
    depth: 'medium',
    schedule: 'immediate'
  });

  const scanTypes = [
    { id: 'web-app', name: 'Web Application Scan', icon: Globe, description: 'Full web application security assessment' },
    { id: 'network', name: 'Network Scan', icon: Database, description: 'Network infrastructure and port scanning' },
    { id: 'api', name: 'API Security Test', icon: Code, description: 'REST/GraphQL API security testing' },
    { id: 'penetration', name: 'Penetration Test', icon: Shield, description: 'Comprehensive penetration testing' },
    { id: 'owasp', name: 'OWASP Top 10', icon: Target, description: 'Test against OWASP vulnerabilities' },
    { id: 'mobile', name: 'Mobile Backend Test', icon: Smartphone, description: 'Mobile app backend security' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.target) {
      toast.error('Please enter a target URL or IP address');
      return;
    }

    const scanId = `SCN-${Math.floor(Math.random() * 1000)}`;
    toast.success(`Scan ${scanId} initiated successfully!`);
    
    setTimeout(() => {
      navigate('/active-scans');
    }, 1500);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="page-container" data-testid="new-scan-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">New Security Scan</h1>
          <p className="page-subtitle">Configure and initiate a new security scan</p>
        </div>
      </div>

      <div className="scan-form-container">
        <form onSubmit={handleSubmit} className="scan-form">
          {/* Target Input */}
          <div className="form-section">
            <h3 className="form-section-title">Target Information</h3>
            <div className="form-group">
              <label className="form-label" htmlFor="target">
                Target URL or IP Address *
              </label>
              <input
                id="target"
                type="text"
                className="form-input"
                placeholder="https://example.com or 192.168.1.0/24"
                value={formData.target}
                onChange={(e) => handleChange('target', e.target.value)}
                data-testid="target-input"
                required
              />
              <p className="form-hint">Enter the target domain, URL, or IP address/range to scan</p>
            </div>
          </div>

          {/* Scan Type Selection */}
          <div className="form-section">
            <h3 className="form-section-title">Scan Type</h3>
            <div className="scan-type-grid">
              {scanTypes.map((type) => (
                <div
                  key={type.id}
                  className={`scan-type-card ${formData.scanType === type.id ? 'selected' : ''}`}
                  onClick={() => handleChange('scanType', type.id)}
                  data-testid={`scan-type-${type.id}`}
                >
                  <type.icon className="scan-type-icon" />
                  <h4 className="scan-type-name">{type.name}</h4>
                  <p className="scan-type-description">{type.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Scan Depth */}
          <div className="form-section">
            <h3 className="form-section-title">Scan Depth</h3>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="depth"
                  value="light"
                  checked={formData.depth === 'light'}
                  onChange={(e) => handleChange('depth', e.target.value)}
                  data-testid="depth-light"
                />
                <span className="radio-content">
                  <span className="radio-title">Light Scan</span>
                  <span className="radio-description">Quick surface-level scan (~15 min)</span>
                </span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="depth"
                  value="medium"
                  checked={formData.depth === 'medium'}
                  onChange={(e) => handleChange('depth', e.target.value)}
                  data-testid="depth-medium"
                />
                <span className="radio-content">
                  <span className="radio-title">Medium Scan</span>
                  <span className="radio-description">Balanced depth and speed (~1-2 hours)</span>
                </span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="depth"
                  value="deep"
                  checked={formData.depth === 'deep'}
                  onChange={(e) => handleChange('depth', e.target.value)}
                  data-testid="depth-deep"
                />
                <span className="radio-content">
                  <span className="radio-title">Deep Scan</span>
                  <span className="radio-description">Comprehensive analysis (~4-6 hours)</span>
                </span>
              </label>
            </div>
          </div>

          {/* Schedule */}
          <div className="form-section">
            <h3 className="form-section-title">Schedule</h3>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="schedule"
                  value="immediate"
                  checked={formData.schedule === 'immediate'}
                  onChange={(e) => handleChange('schedule', e.target.value)}
                  data-testid="schedule-immediate"
                />
                <span className="radio-content">
                  <span className="radio-title">Start Immediately</span>
                  <span className="radio-description">Begin scan right away</span>
                </span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="schedule"
                  value="scheduled"
                  checked={formData.schedule === 'scheduled'}
                  onChange={(e) => handleChange('schedule', e.target.value)}
                  data-testid="schedule-later"
                />
                <span className="radio-content">
                  <span className="radio-title">Schedule for Later</span>
                  <span className="radio-description">Choose a specific date and time</span>
                </span>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/active-scans')}
              data-testid="cancel-scan-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary btn-large"
              data-testid="start-scan-button"
            >
              <Zap size={20} />
              <span>Start Scan</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
